import { Stack } from '@mui/material'
import { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { Button, useRecordContext } from 'react-admin'
import { CertificateOut, CertificateStatus, CertificateType, TargetSystem } from '../../types/apiRequests'
import UploadIcon from '@mui/icons-material/Upload'
import ScheduleIcon from '@mui/icons-material/Schedule'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ScheduleDialogButton from '../../components/buttons/ScheduleDialogButton'
import CsrDownloadButton from '../../components/buttons/DownloadCsrButton'
import CertificateUploadButton from '../../components/buttons/CertificateUploadButton'
import { InstallNowButton } from '../../components/buttons/InstallNowButton'
import CreateIcon from '@mui/icons-material/Create'
import CertificateDownloadButton from '../../components/buttons/CertificateDownloadButton'
import RenewCertificateButton from '../../components/buttons/RenewCertificateButton'
import { RestrictedContainer } from '../../components'
import { CERTIFICATE_WRITE_SCOPE } from '../../config'
import { getCapabilities } from './targetSystems'


type Emphasis = 'contained' | 'outlined'

const ActionsByStatus: FC = () => {
  const record = useRecordContext<CertificateOut>()
  // Renew only reaches the server with the new CSR download, so it can be cancelled until then.
  const [renewing, setRenewing] = useState(false)
  useEffect(() => setRenewing(false), [record?.id, record?.status])

  const buttons: ReactElement | null = useMemo(() => {
    if (!record) return null
    const installable = getCapabilities(record.targetSystem)?.installable ?? false

    // Only systems we install into (Zaken) get scheduling/install.
    const installButtons = (
      <>
        <ScheduleDialogButton
          certificateId={record.id}
          renderAs={(open) => (
            <Button
              label="resources.certificates.actions.scheduleInstallation"
              variant="contained"
              startIcon={<ScheduleIcon />}
              onClick={open}
            />
          )}
        />
        <InstallNowButton certificateId={record.id} />
      </>
    )

    const isZakenClient = record.type === CertificateType.ZaakClient &&
      record.targetSystem === TargetSystem.ZAKEN

    // Zaken client certificates are issued by the server: renew right away.
    // Others renew via a new CSR download, cancellable until then.
    // `emphasis`: primary (contained) only when renew is the main next step.
    const renewButton = (emphasis: Emphasis): ReactElement => {
      if (isZakenClient) {
        return (
          <RenewCertificateButton
            certificateId={record.id}
            renderAs={(onClick, loading) => (
              <Button
                label="resources.certificates.actions.renew"
                variant={emphasis}
                startIcon={<AutorenewIcon />}
                onClick={onClick}
                disabled={loading}
              />
            )}
          />
        )
      }
      if (renewing) {
        return (
          <>
            <CsrDownloadButton
              certificateId={record.id}
              renew
              renderAs={(open, loading) => (
                <Button
                  label="resources.certificates.actions.renewCsr"
                  variant="contained"
                  startIcon={<CreateIcon />}
                  onClick={open}
                  disabled={loading}
                />
              )}
            />
            <Button label="ra.action.cancel" variant="outlined" onClick={() => setRenewing(false)} />
          </>
        )
      }
      return (
        <Button
          label="resources.certificates.actions.renew"
          variant={emphasis}
          startIcon={<AutorenewIcon />}
          onClick={() => setRenewing(true)}
        />
      )
    }

    switch (record.status) {
      case CertificateStatus.PENDING:
      case CertificateStatus.CSR_READY:
      case CertificateStatus.CSR_DOWNLOADED:
        return (
          <>
            <CsrDownloadButton
              certificateId={record.id}
              renderAs={(open, loading) => (
                <Button
                  label="resources.certificates.actions.createCsr"
                  variant="contained"
                  startIcon={<CreateIcon />}
                  onClick={open}
                  disabled={loading}
                />
              )}
            />
            {/* Upload needs a CSR to sign first. */}
            {record.status !== CertificateStatus.PENDING && (
              <CertificateUploadButton
                certificateId={record.id}
                renderAs={(open, loading) => (
                  <Button
                    label="resources.certificates.actions.uploadCertificate"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={open}
                    disabled={loading}
                  />
                )}
              />
            )}
            {/* During a renew the current certificate stays valid and downloadable. */}
            {record.verloopDatum && <CertificateDownloadButton certificateId={record.id} />}
          </>
        )

      // Not installable (legacy; new uploads become ACTIVE): renew is the only next step.
      case CertificateStatus.CERT_UPLOADED:
        return (
          <>
            {installable ? installButtons : renewButton('contained')}
            <CertificateDownloadButton certificateId={record?.id} />
          </>
        )

      case CertificateStatus.ACTIVE:
      case CertificateStatus.EXPIRED:
        return (
          <>
            {renewButton('contained')}
            <CertificateDownloadButton certificateId={record?.id} />
          </>
        )

      // ERROR is mostly a failed install of a valid cert: retry install, or renew (API allows both).
      case CertificateStatus.ERROR:
        return (
          <>
            {installable ? installButtons : null}
            {renewButton(installable ? 'outlined' : 'contained')}
            <CertificateDownloadButton certificateId={record?.id} />
          </>
        )

      case CertificateStatus.INSTALL_SCHEDULED:
      default:
        return (
          <CertificateDownloadButton certificateId={record?.id} />
        )
    }
  }, [record, renewing])

  return (
    <RestrictedContainer access={[CERTIFICATE_WRITE_SCOPE]}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {buttons}
      </Stack>
    </RestrictedContainer>
  )
}

export default ActionsByStatus
